import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { vnDayKey } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useUrlFilters, type FilterField, type UrlFilters } from './Filters';
import { compactVnd } from './format';

// Shared pieces of the Reports II screens (P1-D17): the filter bar every screen uses (date range,
// agent, source, real depositors, game or category), number and rate cells, KPI tiles with their
// three comparisons, a line chart and the CCU heat map.

export type Options = { agents: { id: string; code: string; displayName: string }[]; sources: string[]; games: string[]; categories: string[] };

export const shiftDay = (d: string, n: number) => new Date(new Date(d + 'T00:00:00Z').getTime() + n * 86_400_000).toISOString().slice(0, 10);
export const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

const CATEGORY: Record<string, string> = { table: 'Bàn chơi', mini: 'Mini game', slot: 'Slot', crash: 'Crash', lottery: 'Xổ số', live: 'Casino live' };
export const categoryLabel = (c: string) => CATEGORY[c] ?? c;

export function useOptions() {
  return useQuery({ queryKey: ['analytics', 'options'], queryFn: () => api.get<Options>('/api/admin/analytics/options'), staleTime: 60_000 });
}

export type ScopeOpts = { dates?: boolean; game?: boolean; scope?: boolean };

export const SCOPE_KEYS = ['from', 'to', 'agent', 'source', 'depositors', 'game', 'category'] as const;

/** URL filters with a default range of `days` VN days ending today. */
export function useReportFilters(days: number, extra: readonly string[] = [], extraDefaults: Record<string, string> = {}) {
  const today = vnDayKey(new Date());
  return useUrlFilters([...SCOPE_KEYS, ...extra], { defaults: { from: shiftDay(today, -(days - 1)), to: today, ...extraDefaults } });
}

export function scopeFields(o: Options | undefined, opts: ScopeOpts = {}): FilterField[] {
  const out: FilterField[] = [];
  if (opts.dates !== false) {
    out.push({ key: 'from', label: 'Từ ngày', kind: 'date' }, { key: 'to', label: 'Đến ngày', kind: 'date' });
  }
  if (opts.scope !== false) {
    out.push(
      { key: 'agent', label: 'Đại lý (cả tuyến dưới)', kind: 'select', options: [['', 'Tất cả'], ...(o?.agents ?? []).map((a) => [a.id, `${a.code} ${a.displayName}`] as const)] },
      { key: 'source', label: 'Nguồn (utm_source)', kind: 'select', options: [['', 'Tất cả'], ['(none)', 'Không có nguồn'], ...(o?.sources ?? []).map((s) => [s, s] as const)] },
      { key: 'depositors', label: 'Người chơi', kind: 'select', options: [['', 'Tất cả'], ['true', 'Chỉ người đã nạp']] },
    );
  }
  if (opts.game) {
    out.push(
      { key: 'game', label: 'Game', kind: 'select', options: [['', 'Tất cả'], ...(o?.games ?? []).map((g) => [g, g] as const)] },
      { key: 'category', label: 'Nhóm game', kind: 'select', options: [['', 'Tất cả'], ...(o?.categories ?? []).map((c) => [c, categoryLabel(c)] as const)] },
    );
  }
  return out;
}

export function Num({ v, digits = 0 }: { v: number | null | undefined; digits?: number }) {
  if (v === null || v === undefined || Number.isNaN(v)) return <span className="muted">-</span>;
  return <span className="num">{v.toLocaleString('vi-VN', { maximumFractionDigits: digits, minimumFractionDigits: 0 })}</span>;
}

/** a ratio (0.25) shown as a percent (25%) */
export function Rate({ v, digits = 1 }: { v: number | null | undefined; digits?: number }) {
  if (v === null || v === undefined || Number.isNaN(v)) return <span className="muted">-</span>;
  return <span className={'num' + (v < 0 ? ' num--neg' : '')}>{(v * 100).toLocaleString('vi-VN', { maximumFractionDigits: digits })}%</span>;
}

export function Hours({ v }: { v: number | null | undefined }) {
  if (v === null || v === undefined) return <span className="muted">-</span>;
  return <span className="num">{v < 48 ? `${v.toLocaleString('vi-VN', { maximumFractionDigits: 1 })} giờ` : `${(v / 24).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} ngày`}</span>;
}

const change = (now: number | null, before: number | null) => {
  if (now === null || before === null) return null;
  if (before === 0) return now === 0 ? 0 : null;
  return (now - before) / Math.abs(before);
};

/** One KPI with its value and the three comparisons of the catalogue (yesterday, weekday last week, MTD). */
export function KpiTile({ label, now, yesterday, lastWeek, mtd, lastMtd, money = false, lead = false }: {
  label: string; now: number | null; yesterday: number | null; lastWeek: number | null; mtd: number | null; lastMtd: number | null; money?: boolean; lead?: boolean;
}) {
  const fmt = (v: number | null) => (v === null ? '-' : money ? '₩' + compactVnd(Math.round(v)) : v.toLocaleString('vi-VN', { maximumFractionDigits: 0 }));
  const d = (a: number | null, b: number | null, l: string) => {
    const c = change(a, b);
    return <span className="kpi__hint">{l} <span className="num">{fmt(b)}</span>{c === null ? null : <> (<span className={'num' + (c < 0 ? ' num--neg' : c > 0 ? ' num--pos' : '')}>{c > 0 ? '+' : ''}{(c * 100).toFixed(0)}%</span>)</>}</span>;
  };
  return (
    <div className={'kpi' + (lead ? ' kpi--lead' : '')}>
      <span className="kpi__label">{label}</span>
      <span className="kpi__value">{fmt(now)}</span>
      {d(now, yesterday, 'hôm qua')}
      {d(now, lastWeek, 'cùng thứ tuần trước')}
      {d(mtd, lastMtd, `từ đầu tháng ${fmt(mtd)}, tháng trước`)}
    </div>
  );
}

export type Series = { label: string; points: (number | null)[] };

/** Estimated width of one character of an axis label (11px IBM Plex Sans), in chart units. */
const TICK_CHAR = 6.4;

/** Plain line chart for ratios and curves (retention, LTV, Pareto); x labels under the plot. */
export function LineChart({ series, labels, format, height = 220, title }: {
  series: Series[]; labels: string[]; format: (v: number) => string; height?: number; title: string;
}) {
  const W = 720, padL = 64, padB = 22, padT = 10, padR = 8;
  const vals = series.flatMap((s) => s.points.filter((p): p is number => p !== null));
  const max = Math.max(0, ...vals), min = Math.min(0, ...vals);
  const span = max - min || 1;
  const n = Math.max(2, labels.length);
  const x = (i: number) => padL + ((W - padL - padR) * i) / (n - 1);
  const y = (v: number) => padT + ((max - v) / span) * (height - padB - padT);
  const every = Math.max(1, Math.ceil(labels.length / 12));
  // nhan duoi truc x: moi `every` nhan va luon co nhan cuoi (ky moi nhat, canh phai: can giua thi bi cat o mep);
  // di tu cuoi ve dau, bo nhan nao de len nhan sau no (nhan thang dai, co dau * cua ky chua tron)
  const ticks: { i: number; anchor: 'middle' | 'end' }[] = [];
  for (let i = labels.length - 1, left = Infinity; i >= 0; i--) {
    const last = i > 0 && i === labels.length - 1;
    if (!last && i % every !== 0) continue;
    const w = labels[i]!.length * TICK_CHAR;
    if ((last ? x(i) : x(i) + w / 2) > left - 4) continue;
    ticks.push({ i, anchor: last ? 'end' : 'middle' });
    left = last ? x(i) - w : x(i) - w / 2;
  }
  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${height}`} width="100%" role="img" aria-label={title} preserveAspectRatio="none">
        {[max, 0, min].filter((v, i, a) => a.indexOf(v) === i).map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} className={t === 0 ? 'chart__zero' : 'chart__grid'} />
            <text x={padL - 8} y={y(t) + 4} className="chart__tick" textAnchor="end">{format(t)}</text>
          </g>
        ))}
        {ticks.map((t) => <text key={t.i} x={x(t.i)} y={height - 6} className="chart__tick" textAnchor={t.anchor}>{labels[t.i]}</text>)}
        {series.map((s, k) => {
          const pts = s.points.map((p, i) => (p === null ? null : `${x(i)},${y(p)}`)).filter(Boolean).join(' ');
          return <polyline key={s.label} points={pts} fill="none" className={`chart__line chart__line--${k % 6}`}><title>{s.label}</title></polyline>;
        })}
      </svg>
      {series.length > 1 ? (
        <figcaption className="chart__legend">
          {series.map((s, k) => <span key={s.label}><i className={`chart__swatch chart__line--${k % 6}`} />{s.label}</span>)}
        </figcaption>
      ) : null}
    </figure>
  );
}

const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
export type HeatCell = { weekday: number; hour: number; avg: number | null; peak: number; samples: number };

/** CCU heat map: VN weekday rows x hour columns, the cell shade is the average online players. */
export function Heatmap({ cells }: { cells: HeatCell[] }) {
  const max = Math.max(1, ...cells.map((c) => c.avg ?? 0));
  const at = (w: number, h: number) => cells.find((c) => c.weekday === w && c.hour === h);
  return (
    <div className="heat" role="table" aria-label="Người chơi online trung bình theo thứ và giờ">
      <div className="heat__row" role="row"><span role="columnheader" />{Array.from({ length: 24 }, (_, h) => <span key={h} role="columnheader" className="heat__hour">{h}</span>)}</div>
      {WEEKDAYS.map((wd, w) => (
        <div key={wd} className="heat__row" role="row">
          <span role="rowheader" className="heat__day">{wd}</span>
          {Array.from({ length: 24 }, (_, h) => {
            const c = at(w, h);
            const v = c?.avg ?? null;
            return (
              <span key={h} role="cell" className="heat__cell" style={{ opacity: v === null ? 0.08 : 0.15 + 0.85 * (v / max) }}
                title={v === null ? `${wd} ${h}h: chưa có số` : `${wd} ${h}h: trung bình ${v.toFixed(1)}, cao nhất ${c!.peak}`} />
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- day / week / month

export const GRAINS = ['day', 'week', 'month'] as const;
export type Grain = (typeof GRAINS)[number];
export const GRAIN_LABEL: Record<Grain, string> = { day: 'Ngày', week: 'Tuần', month: 'Tháng' };
/** Column letter of a period number: D3, W3, M3. */
export const GRAIN_LETTER: Record<Grain, string> = { day: 'D', week: 'W', month: 'M' };

/** Calendar period holding the VN day `d`: the day, its Monday or the 1st of its month. */
export function periodOf(d: string, g: Grain): string {
  if (g === 'day') return d;
  if (g === 'week') return shiftDay(d, -((new Date(d + 'T00:00:00Z').getUTCDay() + 6) % 7));
  return d.slice(0, 8) + '01';
}
/** Start of the period `n` periods after the one starting at `p` (n may be negative). */
export function periodShift(p: string, n: number, g: Grain): string {
  if (g === 'day') return shiftDay(p, n);
  if (g === 'week') return shiftDay(p, 7 * n);
  const m = Number(p.slice(0, 4)) * 12 + Number(p.slice(5, 7)) - 1 + n;
  return `${Math.floor(m / 12)}-${String((m % 12) + 1).padStart(2, '0')}-01`;
}
/** How a period reads in a table: 29/09/2026, Tuần 22/09/2026 (its Monday), Tháng 09/2026. */
export function periodLabel(p: string, g: Grain): string {
  const dmy = `${p.slice(8, 10)}/${p.slice(5, 7)}/${p.slice(0, 4)}`;
  return g === 'day' ? dmy : g === 'week' ? `Tuần ${dmy}` : `Tháng ${p.slice(5, 7)}/${p.slice(0, 4)}`;
}

/** Short chart label: 22/09 (a day, or the Monday of a week), 09/2026 (a month); * marks a week or month the range cuts or that is still running. */
export const periodTick = (p: string, g: Grain, partial?: boolean) =>
  (g === 'month' ? `${p.slice(5, 7)}/${p.slice(0, 4)}` : `${p.slice(8, 10)}/${p.slice(5, 7)}`) + (partial && g !== 'day' ? '*' : '');
/** The word for one period: "theo ngày", "từng tuần". */
export const PERIOD_WORD: Record<Grain, string> = { day: 'ngày', week: 'tuần', month: 'tháng' };
/** Default range of a period view ending at `to` (as the API): the last 30 days, 12 weeks or 12 months. */
export const periodDefaultFrom = (to: string, g: Grain) => (g === 'day' ? shiftDay(to, -29) : periodShift(periodOf(to, g), -11, g));
/** A row the range cut or that is not over yet: marked with * and explained under the table. */
export const PARTIAL_NOTE = 'Kỳ có dấu * chưa trọn: khoảng ngày cắt ngang kỳ đó, hoặc kỳ chưa kết thúc.';

/**
 * The grain of a Thống kê screen whose filters live in the URL (key `grain`, empty = day) and the
 * switch handler: a week or month view opens on the last 12 weeks or months, a day view on 30 days.
 */
export function grainFilter(f: UrlFilters): [Grain, (g: Grain) => void] {
  const grain = (GRAINS as readonly string[]).includes(f.values.grain ?? '') ? (f.values.grain as Grain) : 'day';
  const pick = (g: Grain) => {
    const to = f.values.to || vnDayKey(new Date());
    f.set({ ...f.values, grain: g === 'day' ? '' : g, from: periodDefaultFrom(to, g), to });
  };
  return [grain, pick];
}

/** Row label of a period in a Thống kê table, with * when the range cuts it or it is still running. */
export const periodCell = (p: string, g: Grain, partial?: boolean) => periodLabel(p, g) + (partial && g !== 'day' ? '*' : '');

/** Ngày / Tuần / Tháng switch of a report screen. */
export function GrainSwitch({ value, onChange }: { value: Grain; onChange: (g: Grain) => void }) {
  return (
    <div className="grain" role="radiogroup" aria-label="Xem theo ngày, tuần hoặc tháng">
      <span className="grain__label">Xem theo</span>
      {GRAINS.map((g) => (
        <button key={g} type="button" role="radio" aria-checked={value === g} className={'grain__btn' + (value === g ? ' is-on' : '')} onClick={() => onChange(g)}>
          {GRAIN_LABEL[g]}
        </button>
      ))}
    </div>
  );
}

export function Panel({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="panel section">
      <div className="panel__head"><h2>{title}</h2>{aside}</div>
      {children}
    </section>
  );
}
